// "Connect this email to this Ongoing" — the offers a story carries INSIDE it (busy-my-web#291).
//
// A Suggested Contains→Timeline edge is a question ("does this email belong to this story?"), and
// until now the only place it could be answered was home's review list. The AI Brain spec §9 asks
// for it where the story is: open Virgin Media, see the unlinked email that looks relevant, look at
// the thread, then say yes or no without leaving the page.
//
// The SAME edge, the same two verbs. Nothing here invents an answer: the ids are relationship ids,
// and lib/storyAutomation.js's `confirmPath`/`rejectPath` are what the store calls — so an offer
// answered here is answered on home too, and vice versa. What this module adds is only the row: a
// person reads "<sender> · <subject>" and a reason, not a graph edge.
//
// Pure — no store, no api, nothing mounted.

import { signalsLine } from '@/lib/storyAutomation'

export const TEXT = {
  // The block's heading — the spec's own sentence (§9 row 4), kept verbatim.
  heading: 'Connect this email to this Ongoing',
  openThread: 'Open thread',
  add: 'Add',
  notRelated: 'Not related',
  // The two lists that joined this block when they left home (busy-my-web#295). Their ROWS say what
  // they are ("Create a task from this thread?", "Supplier replied on … — review response") and
  // their verbs are storyAutomation's; only the two headings are new, and they are here because
  // this block is what owns them. Chosen, not designed — no frame heads these lists yet, and one
  // unheaded list under a heading that says "Connect this email…" would read as part of it.
  actionsHeading: 'Suggested next steps',
  repliesHeading: 'Replies to review',
}

/** The story's offers live under `ongoing/{id}` — `GET` only. */
export const OFFERS_PATH = 'offers'

/** Where a thread is read: the email page the row's "Open thread" opens. Null without a thread. */
export function threadRoute(timelineId) {
  const id = String(timelineId || '').trim()
  return id ? `/emails/timeline/${id}` : null
}

/** The service answers an envelope (`{ items: [...] }`); anything else reads as no offers. */
export function offerItems(payload) {
  if (Array.isArray(payload)) return payload
  return Array.isArray(payload?.items) ? payload.items : []
}

/**
 * One offer as the block draws it.
 *
 * The label is "<sender> · <subject>" — who it is from first, because that is what makes an email
 * recognisable at a glance in a story that already knows its own subject. Either half alone still
 * makes a line; an offer with neither is dropped rather than drawn as an empty row.
 */
export function offerRow(item) {
  const relationshipId = String(item?.relationshipId || '').trim()
  if (!relationshipId) return null
  const sender = String(item?.sender || '').trim()
  const subject = String(item?.subject || '').trim()
  const label = [sender, subject].filter(Boolean).join(' · ')
  if (!label) return null
  return {
    id: relationshipId,
    relationshipId,
    timelineId: String(item?.timelineId || '').trim() || null,
    sender,
    subject,
    label,
    reason: String(item?.reason || '').trim(),
    signals: signalsLine(item?.signals),
    confidence: typeof item?.confidence === 'number' ? item.confidence : null,
    threadRoute: threadRoute(item?.timelineId),
  }
}

/** Every offer as a row, junk dropped, in the order the service sent them (newest email first). */
export function offerRows(payload) {
  return offerItems(payload).map(offerRow).filter(Boolean)
}

/** The rows minus one answered id — what the block shows the moment Add / Not related is clicked. */
export function withoutOffer(rows, relationshipId) {
  const id = String(relationshipId || '')
  return (rows || []).filter(r => r && String(r.id) !== id)
}
